// lib/color-harmonization-diagnostics.ts

type ColorHarmonizationProfile = 'soft' | 'strict';

interface ColorHarmonizationDimensions {
  width: number;
  height: number;
}

interface ColorHarmonizationRegion {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface ColorHarmonizationMatDiagnostic {
  name: string;
  channels: number | null;
  depth: number | null;
  rows: number | null;
  cols: number | null;
}

interface ColorHarmonizationErrorDetails {
  message: string;
  code: string | number | null;
  stack: string | null;
}

interface ColorHarmonizationDiagnostic {
  stage: string;
  harmonizeProfile: ColorHarmonizationProfile;
  dimensions: {
    source: ColorHarmonizationDimensions;
    analysis: ColorHarmonizationDimensions;
    target: ColorHarmonizationDimensions;
    output: ColorHarmonizationDimensions;
  };
  tile: ColorHarmonizationRegion | null;
  roi: ColorHarmonizationRegion | null;
  mats: ColorHarmonizationMatDiagnostic[];
  error: ColorHarmonizationErrorDetails;
}

interface ColorHarmonizationDiagnosticInput {
  stage: string;
  harmonizeProfile: ColorHarmonizationProfile;
  dimensions: ColorHarmonizationDiagnostic['dimensions'];
  tile?: ColorHarmonizationRegion;
  roi?: ColorHarmonizationRegion;
  mats: ColorHarmonizationMatDiagnostic[];
  error: unknown;
}

type ErrorRecord = Record<string, unknown>;

const asRecord = (value: unknown): ErrorRecord | null => (
  value !== null && typeof value === 'object' ? value as ErrorRecord : null
);

const asText = (value: unknown): string | null => {
  if (typeof value === 'string') return value;
  if (value instanceof String) return value.toString();
  return null;
};

const firstText = (...values: unknown[]): string | null => {
  for (const value of values) {
    const text = asText(value);
    if (text !== null && text.length > 0) return text;
  }
  return null;
};

const asCode = (value: unknown): string | number | null => {
  if (typeof value === 'string' || typeof value === 'number') return value;
  if (value instanceof String) return value.toString();
  return null;
};

const firstCode = (...values: unknown[]): string | number | null => {
  for (const value of values) {
    const code = asCode(value);
    if (code !== null) return code;
  }
  return null;
};

export function normalizeColorHarmonizationError(error: unknown): ColorHarmonizationErrorDetails {
  const record = asRecord(error);
  const nestedRecord = asRecord(record?.error);
  const numericCode = typeof error === 'number' ? error : null;
  const message = firstText(
    record?.message,
    record?.msg,
    record?.err,
    nestedRecord?.message,
    nestedRecord?.msg,
    nestedRecord?.err,
  );

  return {
    message: message ?? (numericCode !== null ? `OpenCV error code ${numericCode}` : 'Unknown harmonization error'),
    code: firstCode(record?.code, nestedRecord?.code, numericCode),
    stack: firstText(record?.stack, nestedRecord?.stack),
  };
}

export function createColorHarmonizationDiagnostic(
  input: ColorHarmonizationDiagnosticInput,
): ColorHarmonizationDiagnostic {
  return {
    stage: input.stage,
    harmonizeProfile: input.harmonizeProfile,
    dimensions: {
      source: { ...input.dimensions.source },
      analysis: { ...input.dimensions.analysis },
      target: { ...input.dimensions.target },
      output: { ...input.dimensions.output },
    },
    tile: input.tile ? { ...input.tile } : null,
    roi: input.roi ? { ...input.roi } : null,
    mats: input.mats.map((mat) => ({ ...mat })),
    error: normalizeColorHarmonizationError(input.error),
  };
}
